import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { updateClinician } from '../services/clinicianService';
import { Clinician } from '../types';

const CLINICIAN_TYPES = [
  { value: 'psicologo_clinico', labelEs: 'Psicólogo/a Clínico/a', labelEn: 'Clinical Psychologist' },
  { value: 'psiquiatra',        labelEs: 'Psiquiatra',             labelEn: 'Psychiatrist' },
  { value: 'neuropsicologo',    labelEs: 'Neuropsicólogo/a',       labelEn: 'Neuropsychologist' },
  { value: 'psicologo_educativo', labelEs: 'Psicólogo/a Educativo/a', labelEn: 'Educational Psychologist' },
  { value: 'psicologo_forense', labelEs: 'Psicólogo/a Forense',   labelEn: 'Forensic Psychologist' },
  { value: 'trabajador_social', labelEs: 'Trabajador/a Social',   labelEn: 'Social Worker' },
  { value: 'terapeuta',         labelEs: 'Terapeuta / Orientador/a', labelEn: 'Therapist / Counselor' },
  { value: 'otro',              labelEs: 'Otro',                   labelEn: 'Other' },
] as const;

const SPECIALIZATIONS = [
  { value: 'adultos',           labelEs: 'Adultos',                       labelEn: 'Adults' },
  { value: 'infancia_adolescencia', labelEs: 'Infancia y Adolescencia',   labelEn: 'Children & Adolescents' },
  { value: 'familia_pareja',    labelEs: 'Familia y Pareja',              labelEn: 'Family & Couples' },
  { value: 'neuropsicologia',   labelEs: 'Neuropsicología',               labelEn: 'Neuropsychology' },
  { value: 'forense',           labelEs: 'Psicología Forense',            labelEn: 'Forensic Psychology' },
  { value: 'salud_mental_grave', labelEs: 'Salud Mental Grave (SMG)',     labelEn: 'Severe Mental Health' },
  { value: 'educativa',         labelEs: 'Psicología Educativa',          labelEn: 'Educational Psychology' },
  { value: 'otro',              labelEs: 'Otra',                          labelEn: 'Other' },
] as const;

interface OnboardingProfileDialogProps {
  clinician: Clinician;
  onComplete: (updated: Clinician) => void;
}

export default function OnboardingProfileDialog({ clinician, onComplete }: OnboardingProfileDialogProps) {
  const { i18n } = useTranslation();
  const { setLanguage } = useLanguage();
  const lang = i18n.language?.split('-')[0] === 'en' ? 'en' : 'es';

  const [name, setName] = useState(clinician.name || '');
  const [clinicianType, setClinicianType] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [selectedLang, setSelectedLang] = useState<'es' | 'en'>(lang as 'es' | 'en');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const t = (es: string, en: string) => selectedLang === 'en' ? en : es;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = t('El nombre es obligatorio', 'Name is required');
    if (!clinicianType) e.clinicianType = t('Selecciona tu tipo de profesional', 'Select your professional type');
    if (!specialization) e.specialization = t('Selecciona tu área principal', 'Select your main area');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Apply language preference immediately
      if (selectedLang !== lang) {
        setLanguage(selectedLang);
      }

      const updated = await updateClinician(clinician.id, {
        name: name.trim(),
        clinicianType,
        specialization,
      });

      if (!updated) {
        setSaveError(t(
          'No se pudo guardar el perfil. Por favor inténtalo de nuevo.',
          'Could not save the profile. Please try again.'
        ));
        return;
      }

      onComplete(updated);
    } catch (err) {
      console.error('Error saving onboarding profile:', err);
      setSaveError(t(
        'Error al guardar. Por favor inténtalo de nuevo.',
        'Error saving. Please try again.'
      ));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = (field: string) =>
    `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors ${
      errors[field]
        ? 'border-red-400 focus:ring-red-200'
        : 'border-slate-300 focus:ring-blue-200 focus:border-blue-400'
    }`;

  const selectCls = (field: string) =>
    `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors bg-white ${
      errors[field]
        ? 'border-red-400 focus:ring-red-200'
        : 'border-slate-300 focus:ring-blue-200 focus:border-blue-400'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl">
              🧠
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">
                {t('Completa tu perfil profesional', 'Complete your professional profile')}
              </h1>
              <p className="text-blue-100 text-xs mt-0.5">
                {t('Solo tarda 30 segundos · No podrás omitir este paso', 'Just 30 seconds · You cannot skip this step')}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">
          {/* Language selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {t('Idioma preferido', 'Preferred language')}
            </label>
            <div className="flex gap-2">
              {(['es', 'en'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setSelectedLang(l)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                    selectedLang === l
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  }`}
                >
                  {l === 'es' ? '🇪🇸  Español' : '🇬🇧  English'}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              {t('Nombre completo', 'Full name')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }}
              placeholder={t('Ej: María García López', 'E.g. John Smith')}
              className={inputCls('name')}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Clinician type */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              {t('Tipo de profesional', 'Professional type')} <span className="text-red-500">*</span>
            </label>
            <select
              value={clinicianType}
              onChange={(e) => { setClinicianType(e.target.value); setErrors(prev => ({ ...prev, clinicianType: '' })); }}
              className={selectCls('clinicianType')}
            >
              <option value="">{t('Selecciona...', 'Select...')}</option>
              {CLINICIAN_TYPES.map((ct) => (
                <option key={ct.value} value={ct.value}>
                  {selectedLang === 'en' ? ct.labelEn : ct.labelEs}
                </option>
              ))}
            </select>
            {errors.clinicianType && <p className="text-red-500 text-xs mt-1">{errors.clinicianType}</p>}
          </div>

          {/* Specialization */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              {t('Área principal de trabajo', 'Main work area')} <span className="text-red-500">*</span>
            </label>
            <select
              value={specialization}
              onChange={(e) => { setSpecialization(e.target.value); setErrors(prev => ({ ...prev, specialization: '' })); }}
              className={selectCls('specialization')}
            >
              <option value="">{t('Selecciona...', 'Select...')}</option>
              {SPECIALIZATIONS.map((sp) => (
                <option key={sp.value} value={sp.value}>
                  {selectedLang === 'en' ? sp.labelEn : sp.labelEs}
                </option>
              ))}
            </select>
            {errors.specialization && <p className="text-red-500 text-xs mt-1">{errors.specialization}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5">
          {saveError && (
            <p className="text-red-500 text-xs mb-3 text-center">{saveError}</p>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed text-sm shadow-lg shadow-blue-200"
          >
            {saving
              ? t('Guardando...', 'Saving...')
              : t('Comenzar a usar Psymetrika →', 'Start using Psymetrika →')}
          </button>
          <p className="text-center text-xs text-slate-400 mt-3">
            {t(
              'Podrás actualizar esta información en cualquier momento desde tu Perfil.',
              'You can update this information anytime from your Profile.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
